import { trackToolAction } from "../../../utils/analytics";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { apiService } from "../../../api/api";
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";
import { Card } from "primereact/card";
import "../../YouTubeScorecard/Components/FilterBars/auditFilters.css";
import { withExportDate } from "../../../utils/exportFileName";

interface CompositionAssetAuditTableProps {
  // Define your component props here
}

const FilterBar = ({
  filters,
  setFilters,
  unfilteredData,
  onSearch,
  onReset,
  onExport,
}: any) => {
  const legalEntityOptions = Array.from(
    new Set(unfilteredData.map((item: any) => item.LegalEntity))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value }));

  const legalEntityTypeOptions = Array.from(
    new Set(unfilteredData.map((item: any) => item.LegalEntityType))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value }));

  return (
    <>
      <div className="grid-container" style={{ marginTop: "-1rem", marginBottom: "1rem" }}>
        <div className="filter-item">
          <text style={{ color: "grey", fontSize: "small" }}>
            Legal Entity
          </text>
          <MultiSelect
            value={filters.legalEntity ? [filters.legalEntity] : []}
            options={legalEntityOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                legalEntity: e.value[0] || "",
              }))
            }
            maxSelectedLabels={1}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <text style={{ color: "grey", fontSize: "small" }}>
            Legal Entity Type
          </text>
          <MultiSelect
            value={filters.legalEntityType ? [filters.legalEntityType] : []}
            options={legalEntityTypeOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                legalEntityType: e.value[0] || "",
              }))
            }
            maxSelectedLabels={1}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
      </div>
      <div className="grid-container">
        <div className="flex-row grid gap-5" style={{ marginLeft: "0.1vh" }}>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button label="Search" icon="pi pi-search" onClick={onSearch} />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Reset"
              icon="pi pi-replay"
              severity="danger"
              onClick={onReset}
            />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Export"
              icon="pi pi-file"
              onClick={onExport}
              severity="success"
            />
          </div>
        </div>
      </div>
    </>
  );
};

const CompositionAssetAuditTable: React.FC<CompositionAssetAuditTableProps> = (
  props
) => {
  const dt = useRef<DataTable<any[]>>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [unfilteredData, setUnfilteredData] = useState<any[]>([]);
  const [filteredRows, setFilteredRows] = useState<any[]>([]);
  const [filters, setFilters] = useState<any>({
    legalEntity: "",
    legalEntityType: "",
  });

  const exportCSV = () => {
    trackToolAction("DataHealthTool", "export");
    dt?.current?.exportCSV();
  };

  useEffect(() => {
    apiService.musicAssetAudit.getIncorrectOwnershipSync().then((data: any) => {
      console.log(data);
      const documents = data.data.documents || [];
      setUnfilteredData(documents);
      setRows(documents);
      setFilteredRows(documents);
    });
  }, []);

  const handleSearch = useCallback(() => {
    trackToolAction("DataHealthTool", "search");
    let filtered = rows;
    if (filters.legalEntity)
      filtered = filtered.filter(
        (r) => r.LegalEntity === filters.legalEntity
      );
    if (filters.legalEntityType)
      filtered = filtered.filter(
        (r) => r.LegalEntityType === filters.legalEntityType
      );
    setFilteredRows(filtered);
  }, [filters, rows]);

  const handleReset = () => {
    setFilters({
      legalEntity: "",
      legalEntityType: "",
    });
    setFilteredRows(rows);
  };

  return (
    <div style={{ width: "100%" }}>
      <Card
        className="flex-column md:flex-col gap-3"
        style={{
          marginTop: "0.3rem",
          marginBottom: "2rem",
          boxShadow: "0px 4px 11px rgba(0, 0, 0, 0.1)",
          borderRadius: "7px",
        }}
      >
        <FilterBar
          filters={filters}
          setFilters={setFilters}
          unfilteredData={unfilteredData}
          onSearch={handleSearch}
          onReset={handleReset}
          onExport={exportCSV}
        />
        <DataTable
          ref={dt}
          exportFilename={withExportDate("Sync Ownership Audit")}
          value={filteredRows}
          paginator
          size="small"
          paginatorLeft
          paginatorTemplate="firstPageLink PrevPageLink PageLinks NextPageLink LastPageLink  "
          showGridlines
          cellSelection
          removableSort
          sortMode="multiple"
          selectionMode="single"
          tableStyle={{ width: "100%", fontSize: "0.8rem" }}
          filterDisplay="row"
          rows={20}
        >
        <Column field="Song_Name" header="Song Name" sortable />
        <Column
          field="Approx_daily_views"
          header="Approx Daily Views"
          sortable
        />
        <Column field="ISRC" header="ISRC" sortable />
        <Column field="Custom_ID" header="Custom ID" sortable />
        <Column field="Asset_ID" header="Asset ID" sortable />
        <Column field="Composition_ID" header="Composition ID" sortable />
        <Column field="UMGWorkCode" header="UMG Work Code" sortable />
        <Column field="Ownership_ratio" header="Ownership" sortable />
        <Column field="No_of_countries" header="Countries" sortable />
        <Column field="No_of_owners" header="Owners" sortable />
        {unfilteredData.some((item: any) => item.LegalEntity) && (
          <Column field="LegalEntity" header="Legal Entity" sortable />
        )}
        {unfilteredData.some((item: any) => item.LegalEntityType) && (
          <Column field="LegalEntityType" header="Legal Entity Type" sortable />
        )}
      </DataTable>
      </Card>
    </div>
  );
};

export default CompositionAssetAuditTable;
